import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../../../Settings/SettingsProvider';
import {
  createLayerOrderingFromFavorites,
  LayerConfig,
} from './layerLoadingUtils';
import { useWMSSettings } from '../../../WMSLayers/useWMSSettings';
import { useCallback } from 'react';
import { useToast } from '@ais-ui/ui-core';
import { areSourceIdentifiersShowOnStartUp } from '../../Coverage/areSourceIdentifiersShownOnStartUp';

export const useLayerOrderingSettings = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { enabledWmsLayers } = useWMSSettings();

  const saveLayerOrderingToSettings = useCallback(
    (layerOrdering: LayerConfig[]) => {
      const settingsToSave = {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          layer_ordering: {
            version: 1,
            layers: layerOrdering,
          },
        },
      };

      mutateSettings(settingsToSave);
    },
    [settings, mutateSettings]
  );

  const createInitialLayerOrdering = useCallback(() => {
    return createLayerOrderingFromFavorites(settings, enabledWmsLayers);
  }, [settings, enabledWmsLayers]);

  const getExistingLayerOrdering = useCallback((): LayerConfig[] | null => {
    const ordering = settings['ribbonPlugin.settings']?.layer_ordering?.layers;
    if (!ordering || ordering.length === 0) {
      return null;
    }

    // Get current favorites
    const favoriteShapes =
      settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];

    const filteredOrdering = ordering.filter((layer: LayerConfig) => {
      // Always keep ships layer
      if (layer.type === 'ships') return true;

      // Check if shape is still in favorites
      if (layer.type === 'shape') {
        return favoriteShapes.includes(layer.id);
      }

      // Check if WMS layer is still enabled
      if (layer.type === 'wms') {
        const wmsLayer = enabledWmsLayers.find(
          (wms: any) => wms.wmsLayerInfo.id === layer.id
        );
        return !!wmsLayer;
      }

      // Check if coverage is still enabled
      if (layer.type === 'coverage' && layer.id === 'source-identifiers') {
        return areSourceIdentifiersShowOnStartUp(settings);
      }

      return false;
    });

    // Find missing favorite layers that aren't in the current ordering
    const missingFavorites: LayerConfig[] = [];

    // Check for missing favorite shapes
    favoriteShapes.forEach((shapeId: string) => {
      const exists = filteredOrdering.some(
        (layer: LayerConfig) => layer.type === 'shape' && layer.id === shapeId
      );
      if (!exists) {
        missingFavorites.push({
          type: 'shape',
          id: shapeId,
          enabled: true,
        });
      }
    });

    // Check for missing WMS layers
    enabledWmsLayers.forEach((wms: any) => {
      const exists = filteredOrdering.some(
        (orderingLayer: LayerConfig) =>
          orderingLayer.type === 'wms' &&
          orderingLayer.id === wms.wmsLayerInfo.id
      );

      if (!exists) {
        missingFavorites.push({
          type: 'wms',
          id: wms.wmsLayerInfo.id,
          enabled: true,
        });
      }
    });

    // Check for missing coverage layer
    if (areSourceIdentifiersShowOnStartUp(settings)) {
      const exists = filteredOrdering.some(
        (orderingLayer: LayerConfig) =>
          orderingLayer.type === 'coverage' &&
          orderingLayer.id === 'source-identifiers'
      );
      if (!exists) {
        missingFavorites.push({
          type: 'coverage',
          id: 'source-identifiers',
          enabled: true,
        });
      }
    }

    // If there are missing favorites, add them to the beginning of the ordering
    if (missingFavorites.length > 0) {
      return [...missingFavorites, ...filteredOrdering];
    }

    return filteredOrdering;
  }, [settings, enabledWmsLayers]);

  return {
    settings, // Return settings so other hooks can use them
    saveLayerOrderingToSettings,
    createInitialLayerOrdering,
    getExistingLayerOrdering,
  };
};

// Hook for getting favorite layer data
export const useFavoriteLayerData = () => {
  const { settings } = useSettings();
  const { enabledWmsLayers } = useWMSSettings();

  const favoriteShapes =
    settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];
  const favoriteFilters =
    settings['ribbonPlugin.settings']?.favorite_filters?.filters || [];

  return {
    favoriteShapes,
    favoriteFilters,
    enabledWmsLayers,
  };
};

// Hook for shape management utilities
export const useShapeManagement = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();

  const removeShapeFromFavorites = useCallback(
    (shapeIdToRemove: string) => {
      const settingsToSave = {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          favorite_shapes: {
            ...settings['ribbonPlugin.settings']?.favorite_shapes,
            shapes:
              settings[
                'ribbonPlugin.settings'
              ]?.favorite_shapes?.shapes?.filter(
                (id: string) => id !== shapeIdToRemove
              ) || [],
          },
        },
      };

      mutateSettings(settingsToSave, {
        onSuccess: () => {
          toast({
            className: 'update-start-up-shapes-success-toast',
            title: 'Success!',
            description: `Removed invalid shape from favorites`,
          });
        },
        onError: () => {
          toast({
            className: 'update-start-up-shapes-settings-error-toast',
            title: 'Error!',
            description: 'Failed to update favorite shapes settings',
            variant: 'destructive',
          });
        },
      });
    },
    [settings, mutateSettings, toast]
  );

  return {
    removeShapeFromFavorites,
  };
};
